import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useImmer } from 'use-immer';
import * as Yup from 'yup';
import { Button, Modal, Form, Col, Alert } from 'react-bootstrap';
import FormControls, { ConditionalRender } from '../formControls';
import 'yup-phone';
import { UserService } from '../../services';
import { userTypes } from '../../utilities/appConstants';

const validPhone = RegExp(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/i);

const customerUserInitValues = {
    id: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: {
        number: '',
        phoneType: 0
    },
    userType: 1,
    roleId: 1,
    accountId: 0,
    state: 100
}

const customerUserValidation = Yup.object().shape({
    firstName: Yup.string().min(2, 'First name must be greater than two characters.').required('First name is required.'),
    lastName: Yup.string().min(2, 'Last name must be greater than two charcters.').required('Last name is required.'),
    emailAddress: Yup.string().email('Email address is not in the correct format').required('Email address is required.'),
    roleId: Yup
        .number()
        .when("userType", {
            is: userTypes.customerUser,
            then: Yup.number().min(100, 'Role is required')
        }),
    phoneNumber: Yup.object({
        number: Yup.string().matches(validPhone, 'Phone number is not in the correct format').required('Phone number is required'),
        phoneType: Yup.number().min(1, "Phone type is required.").required('Phone type is required')
    })
});

const AddUpdateCustomerUser = (props) => {
    const [saveError, setSaveError] = useImmer('');
    const [title, setTitle] = useImmer('');
    const [customerUser, setCustomerUser] = useImmer(customerUserInitValues);
    const [availableRoles, setAvailableRoles] = useImmer([]);

    const userType = props.model.userType;

    const formik = useFormik({
        initialValues: customerUser,
        validationSchema: customerUserValidation,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
            var returnVal = await props.saveModel(values)
            if (!returnVal.result) {
                setSaveError(returnVal.message);
            } else {
                props.closeModal();
            }
            setSubmitting(false);
        }
    });

    useEffect(() => {
        getAvailableRoles();
    }, [userType]);

    const getAvailableRoles = async () => {
        const userService = new UserService();
        var callResult = await userService.getAvailableRoles(userType);
        if (callResult.result) {            
            setAvailableRoles(callResult.model);
        }
    }

    useEffect(() => {
        setCustomerUser(draft => {
            Object.assign(draft, props.model);
        });
        if (props.userType == userTypes.contact) {
            if (props.model.id > 0) {
                setTitle("Edit customer contact");
            } else {
                setTitle("Create new customer contact")
            }
        } else {
            if (props.model.id > 0) {
                setTitle("Edit customer user");
            } else {
                setTitle("Create new customer user")
            }
        }

    }, [props.model])

    return (
        <>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Body>
                    <Alert variant='danger' show={saveError}>{saveError}</Alert>
                <Form.Row>
                    <ConditionalRender visible={props.accounts && props.accounts.length > 0}>
                        <FormControls.SelectBox
                            label="Account"
                            name="accountId"
                            numeric
                            formHandler={formik}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                formik.setFieldValue("accountId", val, true)
                            }}
                            options={props.accounts}
                        />
                    </ConditionalRender>
                    <Form.Group as={Col} lg="6" md="12">
                        <FormControls.Textbox
                            name="firstName"
                            label="First Name"
                            formHandler={formik} />
                    </Form.Group>
                    <Form.Group as={Col} lg="6" md="12">
                        <FormControls.Textbox
                            name="lastName"
                            label="Last Name"
                            formHandler={formik} />
                    </Form.Group>
                </Form.Row>
                <FormControls.ConditionalRender visible={customerUser.id > 0}>
                    <Form.Row>
                        <FormControls.ButtonGrouping
                            label="State"
                            name="state"
                            size="sm"
                            variant="secondary"
                            formHandler={formik}
                            options={[
                                { value: 100, name: "Active" },
                                { value: -50, name: "Suspended" },
                                { value: -100, name: "Deleted" }]} />
                    </Form.Row>
                </FormControls.ConditionalRender>
                <ConditionalRender visible={customerUser.userType === userTypes.customerUser}>
                    <Form.Row>
                        <Form.Group as={Col} lg="12" md="12">
                            <FormControls.SelectBox
                                label="Role"
                                name="roleId"
                                numeric
                                formHandler={formik}
                                defaultValue={0}
                                showDefault
                                defaultLabel="Select role ..."
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    formik.setFieldValue("roleId", val, true)
                                }}
                                options={availableRoles}
                            />
                        </Form.Group>
                    </Form.Row>
                </ConditionalRender>
                <Form.Row>
                    <Form.Group as={Col} lg="12" md="12">
                        <FormControls.Textbox
                            name="emailAddress"
                            label="Email Address"
                            type="email"
                            formHandler={formik} />
                    </Form.Group>
                </Form.Row>
                <Form.Row>
                    <Form.Group as={Col} lg="6" md="12">
                        <FormControls.PhoneNumber
                            name="phoneNumber.number"
                            label="Phone number"
                            formHandler={formik} />
                    </Form.Group>
                    <Form.Group as={Col} lg="6" md="12">
                        <FormControls.SelectBox
                            label="Phone Type"
                            name="phoneNumber.phoneType"
                            numeric
                            formHandler={formik}
                            defaultValue={0}
                            showDefault
                            defaultLabel="Select Phone Type ..."
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                const event = { target: { name: 'phoneNumber.phoneType', value: val } };
                                formik.handleChange(event);
                                //formik.setFieldValue("phoneType", val, true)
                            }}
                            options={[{ name: "Mobile Phone", id: 1 }, { name: "Landline", id: 2 }]}
                        />
                    </Form.Group>

                </Form.Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="default" onClick={() => props.closeModal()}>Cancel</Button>
                <Button variant="primary" disabled={formik.isSubmitting}  onClick={() => formik.submitForm()}>Save</Button>
            </Modal.Footer>
        </>
    )
}

export default AddUpdateCustomerUser;